import { Clipboard } from '@angular/cdk/clipboard';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { CreateUserInputWithRole, ListUsersGQL, LocationCreateBase, UpdateJobGQL } from 'graphql.generated';
import { merge, of, tap } from 'rxjs';
import { catchError, exhaustMap, map, filter, mergeMap, takeUntil, switchMap, take } from 'rxjs/operators';
import { CUSTOMER_TYPES, JOB_ROLE_MAP } from 'src/app/global.constants';
import { DEFAULT_CUSTOMER_TYPE } from 'src/app/job/job-create/job-create-customer/job-create-customer.reducer';
import { NewOpportunityActions } from 'src/app/new-opportunity/new-opportunity.actions';
import { newOpportunityFeature } from 'src/app/new-opportunity/new-opportunity.feature';
import { FreyaNotificationsService } from 'src/app/services/freya-notifications.service';

import { BrandingActions, createConfigSelector } from 'src/app/state/branding.store';

import { NewOpportunityCustomerActions } from '../../../new-opportunity/new-opportunity-customer/new-opportunity-customer.actions';
import { mapAdditionalContactsToInput } from '../../job-helpers';
import { JobToolActions } from '../../job-tool.actions';
import { selectJob, selectCustomer, selectJobId, selectUpdateJobInputFromCustomerEditor, jobToolFeature } from '../../job-tool.reducer';
import { selectCustomerRole, selectIsJobCreationToolOpen } from '../job-create-state/job-create.selectors';

import { JobCreateCustomerActions } from './job-create-customer.actions';
import { jobCreateCustomerFeature } from './job-create-customer.feature';
import { selectCustomerEdit } from './job-create-customer.selectors';

export const loadJobCustomer = createEffect((
	actions$ = inject(Actions),
	store = inject(Store),
) => {
		return actions$.pipe(
			ofType(
				JobToolActions.jobLoaded,
				JobToolActions.jobDataReset,
				JobCreateCustomerActions.editCustomerButtonClicked,
				JobCreateCustomerActions.setCustomerEmailButtonClicked,
				JobCreateCustomerActions.setCustomerPhoneButtonClicked,
			),
			concatLatestFrom(() => [
				store.select(selectJob),
				store.select(selectIsJobCreationToolOpen),
				store.select(jobToolFeature.selectCustomerMutating),
			]),
			filter(([ action, job, isJobCreationToolOpen, customerMutating ]) => {

				if (!job) {
					return false;
				}

				if (customerMutating) {
					return false;
				}

				if (action.type !== JobToolActions.jobLoaded.type && action.type !== JobToolActions.jobDataReset.type) {
					return true;
				}

				return isJobCreationToolOpen;
			}),
			map(([ _action, job ]) => JobCreateCustomerActions.jobCustomerLoaded({ job })),
		);
}, { functional: true, dispatch: true });

export const searchCustomer = createEffect((
  actions$ = inject(Actions),
    store = inject(Store),
    listUsersGQL = inject(ListUsersGQL),
) => {
  return actions$.pipe(
    ofType(
      JobCreateCustomerActions.customerAutocompleteSearchFired,
      NewOpportunityCustomerActions.customerAutocompleteSearchFired,
    ),
    mergeMap((action) => of(action).pipe(
      concatLatestFrom(() => [
        store.select(selectCustomerRole),
        store.select(jobCreateCustomerFeature.selectCustomerEditor),
      ]),
      // Do not search if autocomplete is no longer focused
      filter(([ { field }, _customerRoleId, { focusedField }]) => field === focusedField),
      switchMap(([ { query, field }, customerRoleId ]) => listUsersGQL.fetch({
        filter: { search: query, roles: [ customerRoleId ]},
        skip: 0,
        limit: 25,
      }).pipe(
        // Ignore server response if input blurs while request is in flight
        takeUntil(actions$.pipe(
          ofType(JobCreateCustomerActions.customerAutocompleteInputBlur),
          filter(({ field: blurredField }) => blurredField === field),
        )),
        map((res) => JobCreateCustomerActions.searchUsersSuccess({
          users: res.data?.usersv2.users || [],
          field,
        })),
      )),
      catchError((error) => of(JobCreateCustomerActions.searchUsersError({
        error
      }))),
    )),
  );
}, { functional:true, dispatch: true });

export const updateCustomerData = createEffect((
  actions$ = inject(Actions),
    store = inject(Store),
    updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(JobCreateCustomerActions.acceptCustomerChanges),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectUpdateJobInputFromCustomerEditor),
          store.select(selectCustomerEdit),
          store.select(selectCustomer),
          store.select(selectCustomerRole),
          store.select(selectJobId),
        ]),
        map(([
          _action,
          updateJobs,
          customerEdit,
          currentCustomer,
          customerRoleId,
          jobId,
        ]): {
          jobId: string;
          customerChanged: boolean;
          updateJobVars: { updateJobs: typeof updateJobs };
        } => {

          const {
            selectedCustomer,
            givenName,
            familyName,
            phone,
            email,
            additionalContacts,
            selectedBillingLocation,
            company,
          } = customerEdit;

          let billingAddress: LocationCreateBase;

          if (selectedBillingLocation) {
            billingAddress = {
              addressLineOne: selectedBillingLocation.addressLineOne,
              addressLineTwo: selectedBillingLocation.addressLineTwo,
              areaCode: selectedBillingLocation.areaCode,
              city: selectedBillingLocation.city,
              country: selectedBillingLocation.country,
              countryJurisdiction: selectedBillingLocation.countryJurisdiction,
            }
          }

          if (selectedBillingLocation?.coordinates) {
            billingAddress.coordinates = {
              latitude: selectedBillingLocation.coordinates.latitude,
              longitude: selectedBillingLocation.coordinates.longitude,
              ...(selectedBillingLocation.coordinates.height && { height: selectedBillingLocation.coordinates.height }),
            }
          }

          if (!selectedCustomer || selectedCustomer.id !== currentCustomer.id) {
            updateJobs.removeUsers = [{
              userId: currentCustomer.id,
              role: JOB_ROLE_MAP.customerRole,
            }];
          }

          // Update selected user
          if (selectedCustomer) {

            updateJobs.setCustomerProfile = {
              familyName,
              givenName,
              phone,
              email,
              additionalContacts: mapAdditionalContactsToInput(additionalContacts ?? []),
            } as typeof updateJobs.setCustomerProfile

            if (company) {
              updateJobs.setCustomerProfile.company = company;
            }

            if (billingAddress) {
              updateJobs.setCustomerProfile.billingAddress = billingAddress;
            }

            // If new user was selected, change job customer
            if (selectedCustomer.id !== currentCustomer.id) {

              updateJobs.addUsers = [{
                userId: selectedCustomer.id,
                role: JOB_ROLE_MAP.customerRole,
              }];
            }

            return {
              jobId,
              customerChanged: selectedCustomer.id !== currentCustomer.id,
              updateJobVars: { updateJobs },
            };
          }

          // Change job customer to new customer
          const newCustomerInput = {
            familyName,
            givenName,
            additionalContacts: mapAdditionalContactsToInput(additionalContacts ?? []),
            role: customerRoleId,
          } as CreateUserInputWithRole

          if (email) {
            newCustomerInput.email = email;
          }

          if (phone) {
            newCustomerInput.phone = phone;
          }

          if (company) {
            newCustomerInput.company = company;
          }

          if (billingAddress) {
            newCustomerInput.billingAddress = billingAddress;
          }

          updateJobs.newCustomers = [ newCustomerInput ];

          return {
            jobId,
            customerChanged: true,
            updateJobVars: { updateJobs },
          };
        }),
        exhaustMap(({ updateJobVars, jobId, customerChanged }) => updateJobGQL.mutate(updateJobVars).pipe(
          map((res) => JobCreateCustomerActions.updateCustomerDataSuccess({
            jobId,
            updatedJob: res.data.updateJobs.jobs[0],
            customSuccessMessage: customerChanged ? 'Successfully changed job customer' : 'Customer updated successfully'
          })),
        )),
        catchError((error) => of(JobCreateCustomerActions.updateCustomerDataError({ error }))),
      )),
    );
  }, { functional: true, dispatch: true });

export const preselectExistingCustomer = createEffect((
  actions$ = inject(Actions),
  router = inject(Router),
) => {
    return actions$.pipe(
      ofType(routerNavigatedAction),
      filter(({ payload: { event: { urlAfterRedirects } } }) => {
        return urlAfterRedirects.startsWith('/jobs/new');
      }),
      map(() => router.getCurrentNavigation()?.extras?.state?.existingCustomer),
      filter(Boolean),
      map((existingCustomer) => JobCreateCustomerActions.existingCustomerPreSelected({ existingCustomer })),
    );
  }, { functional: true, dispatch: true });

export const copyToClipboard = createEffect((
  actions$ = inject(Actions),
  clipboard = inject(Clipboard),
  notifications = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(JobCreateCustomerActions.clipboardButtonClicked),
      tap(({ content }) => {
        clipboard.copy(content);
        notifications.success('Copied to clipboard');
      }),
    );
  }, { functional: true, dispatch: false });

export const setDefaultCustomerType = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {

    const navigateToNewOpportunitySuccess$ = actions$.pipe(
      ofType(routerNavigatedAction),
      filter(({
        payload: { event: { urlAfterRedirects } },
      }) => urlAfterRedirects.startsWith('/jobs/new')),
    );

    const customerTypesChanged$ = actions$.pipe(
      ofType(BrandingActions.brandingUpdated)
    );

    return store.select((state: any) => state?.[newOpportunityFeature.name]).pipe(
      filter(Boolean),
      take(1),
      switchMap(() => merge(navigateToNewOpportunitySuccess$, customerTypesChanged$)),
      concatLatestFrom(() => [
        store.select(newOpportunityFeature.selectControlMeta),
        store.select(createConfigSelector('CUSTOMER_TYPES', CUSTOMER_TYPES)),
      ]),
      filter(([ _action, meta, customerTypes ]) => !meta?.['customerType']?.dirty && customerTypes.includes(DEFAULT_CUSTOMER_TYPE)),
      map(() => NewOpportunityActions.defaultCustomerTypeLoaded())
    );
  }, { functional: true, dispatch: true });
